import cx from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';

import * as styles from './DotHover.css';
import { IHotspotProperties } from '../../../../../../common/types/map';
import { selectActiveFeature } from '../../../../../selectors';
import { useMapContext } from '../../../context';
import { useFeaturesContext } from '../context';

interface IDotHoverProps {
  resultsAvailable: boolean;
  detailsVisible: boolean;
}

interface IIconLayout {
  isSmall: boolean;
  isSuburbanFromKp: boolean;
}

export const DotHover: React.FC<IDotHoverProps> = ({
  resultsAvailable: isResultsAvailable,
  detailsVisible: isDetailsVisible,
}) => {
  const { ymaps, map } = useMapContext();
  const { hotspotsLayer } = useFeaturesContext();
  const selectedFeature = useSelector(selectActiveFeature);
  const iconLayout = React.useRef(({ isSmall, isSuburbanFromKp }: IIconLayout) =>
    ymaps.templateLayoutFactory.createClass(
      `<div class="${cx(styles['dailyrent-container'], styles['container--blue'], {
        [styles['container--small']]: isSmall,
        [styles['container--blue']]: !isSuburbanFromKp,
        [styles['container--green']]: isSuburbanFromKp,
      })}">{% if properties.count > 1 %}{% if properties.count <= 9 %}{{properties.count}}{% else %}9+{% endif %}{% endif %}</div>`,
    ),
  );
  const placemarkObject = React.useRef<YMaps.GeoObject | null>(null);

  const handleHotspotMouseEnter = React.useCallback(
    (event: YMaps.IEvent) => {
      if (!isResultsAvailable || isDetailsVisible) {
        return;
      }

      const activeObject = event.get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject');
      const originalFeature = activeObject.getProperties().originalFeature;

      if (selectedFeature && selectedFeature.id === originalFeature.id) {
        return;
      }

      const isSmallPlacemark = originalFeature.properties.offers.length === 1;

      placemarkObject.current = new ymaps.GeoObject(
        {
          ...originalFeature,
          type: 'Feature',
          id: 'DotHoverFeature',
        },
        {
          iconLayout: iconLayout.current({
            isSmall: isSmallPlacemark,
            isSuburbanFromKp: false,
          }),
        },
      );

      map.geoObjects.add(placemarkObject.current);
    },
    [isDetailsVisible, isResultsAvailable, map, selectedFeature, ymaps],
  );

  const handleHotspotMouseLeave = React.useCallback(() => {
    if (placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
  }, [map]);

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('mouseenter', handleHotspotMouseEnter);
    hotspotsLayer.layer.events.add('mouseleave', handleHotspotMouseLeave);

    return () => {
      hotspotsLayer.layer.events.remove('mouseenter', handleHotspotMouseEnter);
      hotspotsLayer.layer.events.remove('mouseleave', handleHotspotMouseLeave);
    };
  }, [handleHotspotMouseEnter, handleHotspotMouseLeave, hotspotsLayer]);

  React.useEffect(() => {
    if (selectedFeature && placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  return null;
};
